import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Typography,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { adminApi, persistAdminUserFromPayload } from 'src/utils/adminApi';

const STEPS = ['Verify current email', 'New email address', 'Verify new email'];

function normalizeEmail(value) {
  return value.trim().toLowerCase();
}

export default function AdminEmailChangeDialog({ open, onClose, user, onUpdated }) {
  const [activeStep, setActiveStep] = useState(0);
  const [otpCurrent, setOtpCurrent] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [otpNew, setOtpNew] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const autoSendDoneRef = useRef(false);

  const reset = () => {
    setActiveStep(0);
    setOtpCurrent('');
    setNewEmail('');
    setOtpNew('');
    setError('');
    setInfo('');
    autoSendDoneRef.current = false;
  };

  const sendCurrentOtp = useCallback(async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const result = await adminApi.sendEmailChangeCurrentOtp();
      setInfo(result.message || `Verification code sent to ${user?.email}.`);
    } catch (err) {
      setError(err.message || 'Could not send code to current email.');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const handleDialogEnter = () => {
    reset();
    if (!autoSendDoneRef.current) {
      autoSendDoneRef.current = true;
      void sendCurrentOtp();
    }
  };

  const verifyCurrentOtp = async () => {
    if (!otpCurrent.trim()) {
      setError('Enter the code from your current email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await adminApi.verifyEmailChangeCurrentOtp(otpCurrent.trim());
      setInfo('');
      setActiveStep(1);
    } catch (err) {
      setError(err.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const requestNewEmail = async () => {
    const next = normalizeEmail(newEmail);
    const current = normalizeEmail(user?.email || '');
    if (!next) {
      setError('Enter a new email address.');
      return;
    }
    if (next === current) {
      setError('New email must be different from your current email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setError('Enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await adminApi.requestEmailChange(next);
      setInfo(result.message || `Verification code sent to ${next}. Your sign-in email is unchanged until you confirm the code.`);
      setActiveStep(2);
    } catch (err) {
      setError(err.message || 'Could not start email change.');
    } finally {
      setLoading(false);
    }
  };

  const verifyNewOtp = async () => {
    if (!otpNew.trim()) {
      setError('Enter the code from your new email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await adminApi.verifyEmailChangeNewOtp(otpNew.trim());
      const updated = persistAdminUserFromPayload(result);
      onUpdated?.(updated);
      onClose();
    } catch (err) {
      setError(err.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onEnter: handleDialogEnter }} fullWidth maxWidth="sm">
      <DialogTitle>Change email</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Typography variant="body2" color="text.secondary">
            Your account email stays <strong>{user?.email}</strong> until step 3 is completed.
          </Typography>

          {activeStep === 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                Confirm access to your current inbox ({user?.email}).
              </Typography>
              <LoadingButton variant="outlined" loading={loading} onClick={sendCurrentOtp}>
                Send code to current email
              </LoadingButton>
              <TextField
                label="Code from current email"
                fullWidth
                value={otpCurrent}
                onChange={(e) => setOtpCurrent(e.target.value)}
                autoComplete="one-time-code"
              />
            </>
          )}

          {activeStep === 1 && (
            <>
              <Typography variant="body2" color="text.secondary">
                Enter the address you want to use. We will check it is not already registered.
              </Typography>
              <TextField
                label="New email address"
                type="email"
                fullWidth
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                autoComplete="email"
              />
            </>
          )}

          {activeStep === 2 && (
            <>
              <Typography variant="body2" color="text.secondary">
                Enter the code sent to {normalizeEmail(newEmail) || 'your new email'}. Only then will your email
                update.
              </Typography>
              <TextField
                label="Code from new email"
                fullWidth
                value={otpNew}
                onChange={(e) => setOtpNew(e.target.value)}
                autoComplete="one-time-code"
              />
            </>
          )}

          {info && <Alert severity="info">{info}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={loading}>
          Cancel
        </LoadingButton>
        {activeStep === 0 && (
          <LoadingButton variant="contained" loading={loading} onClick={verifyCurrentOtp}>
            Verify current email
          </LoadingButton>
        )}
        {activeStep === 1 && (
          <LoadingButton variant="contained" loading={loading} onClick={requestNewEmail}>
            Send code to new email
          </LoadingButton>
        )}
        {activeStep === 2 && (
          <LoadingButton variant="contained" loading={loading} onClick={verifyNewOtp}>
            Confirm new email
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

AdminEmailChangeDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  user: PropTypes.object,
  onUpdated: PropTypes.func
};
